const PRINTABLE_ASCII_EMAIL_PATTERN = /^[\x21-\x7E]+$/;
const LOCAL_PART_PATTERN = /^[a-z0-9]+(?:\.[a-z0-9]+)*$/i;
const DOMAIN_LABEL_PATTERN =
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const TOP_LEVEL_LABEL_PATTERN = /^[a-z]+$/i;

const MAX_EMAIL_LENGTH = 254;
const MAX_LOCAL_PART_LENGTH = 64;

/**
 * View R's Email-first identifier policy intentionally accepts a narrower
 * subset than RFC 5322. The local part permits ASCII letters, digits, and
 * single separating dots. The domain follows DNS label rules, including an
 * interior hyphen for real company domains such as `byond-r.com`.
 *
 * The top-level label must be at least two ASCII letters. A two-label address
 * on a two-letter country suffix (for example `company.eu`) is a real company
 * address, so the number of domain labels is not part of the rule.
 */
export const isValidEmailAddress = (value: string): boolean => {
  if (
    value.length > MAX_EMAIL_LENGTH ||
    !PRINTABLE_ASCII_EMAIL_PATTERN.test(value)
  ) {
    return false;
  }

  const atIndex = value.indexOf("@");
  if (atIndex <= 0 || atIndex !== value.lastIndexOf("@")) return false;

  const localPart = value.slice(0, atIndex);
  const domain = value.slice(atIndex + 1);
  if (
    localPart.length > MAX_LOCAL_PART_LENGTH ||
    !LOCAL_PART_PATTERN.test(localPart)
  ) {
    return false;
  }

  const domainLabels = domain.split(".");
  if (
    domainLabels.length < 2 ||
    domainLabels.some((label) => !DOMAIN_LABEL_PATTERN.test(label))
  ) {
    return false;
  }

  const topLevelLabel = domainLabels[domainLabels.length - 1] ?? "";
  return (
    TOP_LEVEL_LABEL_PATTERN.test(topLevelLabel) && topLevelLabel.length >= 2
  );
};
